import { memo, useEffect, useRef, useState } from 'react';
import Input from 'components/Input';
import RoundedButton from 'components/RoundedButtons';
import ErrorComponent from 'components/ErrorComponent';
import {
  cancel,
  label_text,
  name,
  otp_placeholder,
  placeholder,
} from '../constants/text';
import { forgotPassword, resendOtp } from 'reduxStore/actions/auth';
import { useDispatch } from 'react-redux';

const ResetPassword = memo((props) => {
  const { formikOTP, formikEmail, onSubmit, isResetPasswordPage, cancelModal } =
    props;

  const inputRefs = useRef([]);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    let interval;

    if (!isResetPasswordPage && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    if (timer === 0) {
      setCanResend(true);
    }

    return () => clearInterval(interval);
  }, [timer, isResetPasswordPage]);

  useEffect(() => {
    if (!isResetPasswordPage) {
      setTimer(60);
      setCanResend(false);
    }
  }, [isResetPasswordPage]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    const otpString = newOtpValues.join('');
    formikOTP.setFieldValue('otp', otpString);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digits = paste.replace(/\D/g, '').slice(0, 6);

    const newOtpValues = [...otpValues];
    for (let i = 0; i < 6; i++) {
      newOtpValues[i] = digits[i] || '';
    }
    setOtpValues(newOtpValues);
    formikOTP.setFieldValue('otp', newOtpValues.join(''));
  };

  const handleResendOtp = async () => {
    const email =
      formikEmail?.values?.email || localStorage.getItem('signupEmail') || '';
    if (!email) {
      console.error('Email is required to resend OTP');
      return;
    }

    try {
      const resultAction = await dispatch(forgotPassword({ email }));
      setTimer(60);
      setCanResend(false);
      if (resendOtp.fulfilled.match(resultAction)) {
        console.log('OTP Resent Successfully:', resultAction.payload);
        // Reset timer
      } else {
        console.error('Resend failed:', resultAction.payload);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
    }
  };
  const handleCancelButton = () => {
    setOtpValues(['', '', '', '', '', '']);
    cancelModal();
  };
  return (
    <form onSubmit={onSubmit}>
      {isResetPasswordPage ? (
        <>
          <Input
            labelText={label_text}
            name={name.email}
            placeholder={placeholder}
            onChange={formikEmail.handleChange}
            isError={formikEmail.errors.email}
            onBlur={formikEmail.handleBlur}
            value={formikEmail.values.email}
          />

          {formikEmail.errors.email ? (
            <ErrorComponent text={formikEmail.errors.email} />
          ) : null}
        </>
      ) : (
        <>
          <div className='mb-4'>
            <div className='flex justify-between items-center gap-2'>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type='text'
                  maxLength='1'
                  value={otpValues[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onBlur={formikOTP.handleBlur}
                  className={`w-9 h-9 sm:w-12 sm:h-12 bg-[#F0F0F0] border-none text-xl text-center rounded-md  transition-colors ${
                    formikOTP.errors.otp
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                  } focus:ring-2 focus:ring-blue-200`}
                />
              ))}
            </div>
          </div>

          {formikOTP.errors.otp ? (
            <ErrorComponent text={formikOTP.errors.otp} />
          ) : null}
        </>
      )}

      {!isResetPasswordPage && (
        <div className='text-center text-white font-medium mt-2'>
          {canResend ? (
            <button
              type='button'
              onClick={handleResendOtp}
              className='text-black'
            >
              Didn’t receive code?{' '}
              <span className='underline text-primary'>Resend</span>
            </button>
          ) : (
            <div className='text-black'>{formatTime(timer)}</div>
          )}
        </div>
      )}

      <RoundedButton
        className='xl:mt-5 md:mt-4 mt-4 mb-6 w-full font-semibold justify-center'
        isWidthFull={true}
        text={isResetPasswordPage ? 'Continue' : 'Submit'}
        type='submit'
      />

      <RoundedButton
        className='w-full font-semibold justify-center !bg-gray-1 bg-opacity-35 text-gray-3'
        isWidthFull={true}
        text={cancel}
        type='button'
        onClick={() => handleCancelButton()}
      />
    </form>
  );
});

export default ResetPassword;
